import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtEur, fileUrl, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Briefcase, Search, Tag, X, ImagePlus } from "lucide-react";

const EMPTY = { nome: "", prezzo_vendita: "", prezzo_rivenditore: "", costo_servizio: "", regime_iva: "iva22", categoria: "aula", validita_anni: "", tags: "", note: "" };

const CATEGORIE = {
  aula: "Corsi Aula",
  videoconferenza: "Corsi VideoConferenza",
  elearning: "Corsi E-learning",
  consulenza: "Consulenza Aziendale",
};
const CAT_CLS = {
  aula: "bg-dusty-50 text-dusty-700 border-dusty-200",
  videoconferenza: "bg-violet-50 text-violet-700 border-violet-200",
  elearning: "bg-teal-50 text-teal-700 border-teal-200",
  consulenza: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Services() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputs = useRef({});

  const load = () => api.get("/services").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const pickImage = (id) => fileInputs.current[id]?.click();
  const onImageSelected = async (id, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Immagine troppo grande (max 10 MB)");
    setUploadingId(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/services/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Immagine aggiornata");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploadingId(null);
    }
  };

  const allTags = useMemo(() => {
    const set = new Set();
    items.forEach((s) => (s.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) =>
      s.nome.toLowerCase().includes(q) || (s.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [items, search]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...s, prezzo_rivenditore: s.prezzo_rivenditore ?? "", validita_anni: s.validita_anni ?? "", tags: (s.tags || []).join(", ") }); setEditId(s.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Il nome è obbligatorio");
    try {
      const tags = (form.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        nome: form.nome, prezzo_vendita: parseFloat(form.prezzo_vendita) || 0,
        prezzo_rivenditore: parseFloat(form.prezzo_rivenditore) || 0,
        costo_servizio: parseFloat(form.costo_servizio) || 0, regime_iva: form.regime_iva,
        categoria: form.categoria || "aula", validita_anni: parseInt(form.validita_anni) || 0, tags, note: form.note,
      };
      if (editId) await api.put(`/services/${editId}`, payload);
      else await api.post("/services", payload);
      toast.success(editId ? "Servizio aggiornato" : "Servizio creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo servizio?")) return;
    await api.delete(`/services/${id}`); toast.success("Servizio eliminato"); load();
  };

  const CAT_ORDER = ["aula", "videoconferenza", "elearning", "consulenza"];
  const renderCard = (s) => (
    <Card key={s.id} className="border-slate-200 shadow-sm overflow-hidden flex flex-col" data-testid={`service-card-${s.id}`}>
      <div className="relative aspect-video bg-dusty-50 group">
        {s.image_path ? (
          <img loading="lazy" decoding="async" src={fileUrl(s.image_thumb_path || s.image_path)} alt={s.nome} className="h-full w-full object-cover" data-testid={`service-image-${s.id}`} />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-dusty-300"><Briefcase className="h-10 w-10" /></div>
        )}
        <input type="file" accept="image/*" className="hidden" ref={(el) => (fileInputs.current[s.id] = el)}
          onChange={(e) => onImageSelected(s.id, e)} data-testid={`service-image-input-${s.id}`} />
        <button onClick={() => pickImage(s.id)} disabled={uploadingId === s.id} data-testid={`service-image-btn-${s.id}`}
          className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-white/90 hover:bg-white text-slate-700 shadow-sm border border-slate-200 transition-colors">
          <ImagePlus className="h-3.5 w-3.5" /> {uploadingId === s.id ? "Carico…" : (s.image_path ? "Cambia foto" : "Aggiungi foto")}
        </button>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between">
          <div className="font-semibold text-slate-800">{s.nome}</div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
            <Button size="icon" variant="ghost" onClick={() => del(s.id)} data-testid={`delete-service-${s.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-slate-500">Prezzo</span><span className="font-semibold text-slate-800">{fmtEur(s.prezzo_vendita)}</span>
        </div>
        {s.prezzo_rivenditore > 0 && (
          <div className="mt-1 flex items-center justify-between text-sm" data-testid={`service-prezzo-riv-${s.id}`}>
            <span className="text-violet-600">Prezzo rivenditori</span><span className="font-semibold text-violet-700">{fmtEur(s.prezzo_rivenditore)}</span>
          </div>
        )}
        <div className="mt-1 flex items-center justify-between text-sm">
          <span className="text-slate-500">Costo</span><span className="text-slate-600">{fmtEur(s.costo_servizio)}</span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <Badge className={`border ${CAT_CLS[s.categoria] || CAT_CLS.aula} hover:opacity-100`} data-testid={`service-cat-${s.id}`}>{CATEGORIE[s.categoria] || CATEGORIE.aula}</Badge>
          {s.regime_iva === "esente"
            ? <Badge className="bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-50">Esente IVA</Badge>
            : <Badge className="bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-100">IVA 22%</Badge>}
          {(s.tags || []).map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-dusty-50 text-dusty-700 border border-dusty-200" data-testid={`service-tag-${t}`}>
              <Tag className="h-3 w-3" />{t}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Catalogo Servizi</h1>
          <p className="text-sm text-slate-500 mt-1">Servizi offerti raggruppati per categoria, con prezzo, costo, regime IVA e tag.</p>
        </div>
        <Button onClick={openNew} data-testid="add-service-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo Servizio
        </Button>
      </div>

      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="service-search"
            placeholder="Cerca per nome o tag…" className="pl-9 bg-white" />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="service-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-slate-400">Tag:</span>
            {allTags.map((t) => (
              <button key={t} onClick={() => setSearch(t)} data-testid={`tag-filter-${t}`}
                className="text-xs px-2.5 py-1 rounded-full bg-dusty-50 text-dusty-700 border border-dusty-200 hover:bg-dusty-100 transition-colors">
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-8">
        {CAT_ORDER.map((cat) => {
          const list = filtered.filter((s) => (s.categoria || "aula") === cat);
          if (list.length === 0) return null;
          return (
            <div key={cat} data-testid={`service-group-${cat}`}>
              <h2 className="text-lg font-display font-semibold text-slate-800 mb-3 flex items-center gap-2">
                {CATEGORIE[cat]}<span className="text-xs font-normal text-slate-400">({list.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map(renderCard)}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-slate-400 py-12 text-center">{items.length === 0 ? "Nessun servizio. Aggiungine uno." : "Nessun servizio trovato per questa ricerca."}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="service-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifica Servizio" : "Nuovo Servizio"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome servizio *</Label><Input value={form.nome} onChange={set("nome")} data-testid="service-nome" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Prezzo di vendita (€)</Label><Input type="number" step="0.01" value={form.prezzo_vendita} onChange={set("prezzo_vendita")} data-testid="service-prezzo" className="mt-1.5" /></div>
              <div><Label>Costo del servizio (€)</Label><Input type="number" step="0.01" value={form.costo_servizio} onChange={set("costo_servizio")} data-testid="service-costo" className="mt-1.5" /></div>
            </div>
            <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3">
              <Label className="text-violet-800">Prezzo riservato rivenditori (€)</Label>
              <Input type="number" step="0.01" value={form.prezzo_rivenditore} onChange={set("prezzo_rivenditore")} data-testid="service-prezzo-rivenditore" className="mt-1.5 bg-white" placeholder="Lascia 0 per usare il prezzo di vendita" />
              <p className="text-xs text-violet-600/80 mt-1">Prezzo dedicato ai partner rivenditori (diverso da quello degli intermediari). Se 0, viene usato il prezzo di vendita standard.</p>
            </div>
            <div>
              <Label>Categoria</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="service-categoria">
                {Object.entries(CATEGORIE).map(([key, label]) => {
                  const active = form.categoria === key;
                  return (
                    <button key={key} type="button" onClick={() => setForm({ ...form, categoria: key })}
                      data-testid={`categoria-btn-${key}`}
                      className={`text-sm px-3 py-2 rounded-lg border text-left transition-colors ${active ? `${CAT_CLS[key]} font-semibold shadow-sm` : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Regime IVA</Label>
                <Select value={form.regime_iva} onValueChange={(v) => setForm({ ...form, regime_iva: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="service-iva"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="iva22">IVA 22%</SelectItem>
                    <SelectItem value="esente">Esente IVA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Validità attestato (anni)</Label>
                <Input type="number" min="0" value={form.validita_anni} onChange={set("validita_anni")} data-testid="service-validita" className="mt-1.5" placeholder="es. 5 (0 = nessuna)" />
                <p className="text-xs text-slate-400 mt-1">La scadenza dell'attestato sarà calcolata dalla data di emissione + questi anni.</p>
              </div>
            </div>
            <div>
              <Label>Tag (separati da virgola)</Label>
              <Input value={form.tags} onChange={set("tags")} data-testid="service-tags" placeholder="es. sicurezza, corso, online" className="mt-1.5" />
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={set("note")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="service-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
